// API service for Love Your 401K

// TEMPORARY: Hardcoded for production deployment testing
const API_BASE_URL = 'https://api.loveyour401k.com/api';

console.log('API Base URL:', API_BASE_URL); // For debugging

const api = {
  // Search for companies
  search: async (query) => {
    const response = await fetch(`${API_BASE_URL}/search?query=${encodeURIComponent(query)}`);
    if (!response.ok) {
      throw new Error('Search failed');
    }
    return response.json();
  },

  // Get plans for a company
  getPlans: async (ein) => {
    const response = await fetch(`${API_BASE_URL}/plans/${ein}`);
    if (!response.ok) {
      throw new Error('Failed to fetch plans');
    }
    return response.json();
  },

  // Get plan details
  getPlanDetails: async (ein, planNumber, planYear) => {
    const response = await fetch(
      `${API_BASE_URL}/plans/${ein}/${planNumber}/${planYear}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch plan details');
    }
    return response.json();
  },

  // Get historical data for a plan
  getHistoricalData: async (ein, planNumber) => {
    const response = await fetch(
      `${API_BASE_URL}/history/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch historical data');
    }
    return response.json();
  },

  // Alias for getPlanHistory (used by PlanDetails.jsx)
  getPlanHistory: async (ein, planNumber) => {
    const response = await fetch(
      `${API_BASE_URL}/history/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch historical data');
    }
    return response.json();
  },

  // Get peer comparison data
  getPeerComparison: async (ein, planNumber, planYear) => {
    const response = await fetch(
      `${API_BASE_URL}/comparison/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch peer comparison');
    }
    return response.json();
  },

  // Get scatter plot data for peer comparison
  getScatterPlotData: async (ein, planNumber, planYear) => {
    const response = await fetch(
      `${API_BASE_URL}/scatter/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch scatter plot data');
    }
    return response.json();
  },

  // Get scenario projections
  getScenarios: async (ein, planNumber, analysisYear) => {
    const response = await fetch(
      `${API_BASE_URL}/scenarios/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch scenarios');
    }
    return response.json();
  },

  // Get lookback scenarios (legacy — Phase 5 engine, used by B2B tool only)
  getLookbackScenarios: async (ein, planNumber, analysisYear) => {
    const response = await fetch(
      `${API_BASE_URL}/lookback/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch lookback scenarios');
    }
    return response.json();
  },

  // Get peer-median lookback (corrected economic-impact math for the consumer
  // page). Returns 5/10/15/max windows plus a `headline` block picking the
  // best window for display. See Dev_Code/PHASE5-FIX-HANDOFF.md.
  getPeerLookback: async (ein, planNumber) => {
    const response = await fetch(
      `${API_BASE_URL}/peer-lookback/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch peer-median lookback');
    }
    return response.json();
  },

  // Get contribution cost analysis
  getContributionCostAnalysis: async (ein, planNumber) => {
    const response = await fetch(
      `${API_BASE_URL}/contribution-cost-analysis/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch contribution cost analysis');
    }
    return response.json();
  },

  // Get plan status
  getPlanStatus: async (ein, planNumber) => {
    const response = await fetch(
      `${API_BASE_URL}/plan-status/${ein}/${planNumber}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch plan status');
    }
    return response.json();
  },

  // Plan Estimator - Search NAICS codes
  searchNAICS: async (query) => {
    const response = await fetch(
      `${API_BASE_URL}/estimator/naics-search?query=${encodeURIComponent(query)}`
    );
    if (!response.ok) {
      throw new Error('NAICS search failed');
    }
    return response.json();
  },

  // Plan Estimator - Get estimate
  getEstimate: async (params) => {
    const response = await fetch(`${API_BASE_URL}/estimator/estimate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error('Failed to get estimate');
    }
    return response.json();
  },

  // Research - Get list of industries
  getResearchIndustries: async () => {
    const response = await fetch(`${API_BASE_URL}/research/industries`);
    if (!response.ok) {
      throw new Error('Failed to fetch industries');
    }
    return response.json();
  },

  // Research - Get industry trends
  getIndustryTrends: async (industryCode, startYear = 2009, endYear = 2024, sizeCategory = null) => {
    let url = `${API_BASE_URL}/research/industry-trends?industry_code=${industryCode}&start_year=${startYear}&end_year=${endYear}`;
    if (sizeCategory) {
      url += `&size_category=${encodeURIComponent(sizeCategory)}`;
    }
    console.log('=== API CALL: getIndustryTrends ===');
    console.log('URL:', url);
    console.log('Parameters:', { industryCode, startYear, endYear, sizeCategory });
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch industry trends');
    }
    const data = await response.json();
    console.log('Response data length:', data.length);
    console.log('First item:', data[0]);
    return data;
  },

  // Research - Get industry summary
  getIndustrySummary: async (industryCode, year = 2024) => {
    const response = await fetch(
      `${API_BASE_URL}/research/industry-summary?industry_code=${industryCode}&year=${year}`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch industry summary');
    }
    return response.json();
  },

  // Prospecting - Search for prospects
  searchProspects: async (filters) => {
    const response = await fetch(`${API_BASE_URL}/prospecting/search`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(filters),
    });
    if (!response.ok) {
      throw new Error('Failed to search prospects');
    }
    return response.json();
  },

  // Prospecting - Get list of states
  getProspectingStates: async () => {
    const response = await fetch(`${API_BASE_URL}/prospecting/states`);
    if (!response.ok) {
      throw new Error('Failed to fetch states');
    }
    return response.json();
  },

  // Prospecting - Export prospects
  exportProspects: async (filters) => {
    const params = new URLSearchParams();
    if (filters.min_admin_cost_per_participant) params.append('min_admin_cost_per_participant', filters.min_admin_cost_per_participant);
    if (filters.max_admin_cost_per_participant) params.append('max_admin_cost_per_participant', filters.max_admin_cost_per_participant);
    if (filters.min_admin_cost_pct_of_assets) params.append('min_admin_cost_pct_of_assets', filters.min_admin_cost_pct_of_assets);
    if (filters.max_admin_cost_pct_of_assets) params.append('max_admin_cost_pct_of_assets', filters.max_admin_cost_pct_of_assets);
    if (filters.min_participants) params.append('min_participants', filters.min_participants);
    if (filters.max_participants) params.append('max_participants', filters.max_participants);
    if (filters.min_assets) params.append('min_assets', filters.min_assets);
    if (filters.max_assets) params.append('max_assets', filters.max_assets);
    if (filters.states && filters.states.length > 0) params.append('states', filters.states.join(','));
    if (filters.size_category) params.append('size_category', filters.size_category);
    if (filters.limit) params.append('limit', filters.limit);
    
    const response = await fetch(`${API_BASE_URL}/prospecting/export?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to export prospects');
    }
    return response.json();
  },
  // Cohort Analysis - NAICS autocomplete search
  searchCohortNaics: async (query, level = 4) => {
    const params = new URLSearchParams({ q: query, level });
    const response = await fetch(`${API_BASE_URL}/cohort/naics-search?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to search NAICS codes');
    }
    return response.json();
  },

  // Cohort Analysis - count plans matching cohort criteria
  getCohortPreview: async (naics, sizeCategory, effectiveYear, level = 4) => {
    const params = new URLSearchParams({
      naics,
      size_category: sizeCategory,
      effective_year: effectiveYear,
      level,
    });
    const response = await fetch(`${API_BASE_URL}/cohort/preview?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to load cohort preview');
    }
    return response.json();
  },

  // Cohort Analysis - full cohort analysis with retention and metrics
  getCohortAnalysis: async (naics, sizeCategory, effectiveYear, level = 4, page = 1, perPage = 50) => {
    const params = new URLSearchParams({
      naics,
      size_category: sizeCategory,
      effective_year: effectiveYear,
      level,
      page,
      per_page: perPage,
    });
    const response = await fetch(`${API_BASE_URL}/cohort/analysis?${params.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to load cohort analysis');
    }
    return response.json();
  },

  // Download PDF report for a plan
  downloadPlanPDF: async (ein, planNumber, planYear) => {
    const response = await fetch(
      `${API_BASE_URL}/plan-analysis/${ein}/${planNumber}/${planYear}/pdf`
    );
    if (!response.ok) {
      throw new Error('Failed to generate PDF report');
    }
    return response.blob();
  },
};

export default api;
export { API_BASE_URL };
